import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, ListItemIcon, ListItemText, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { User } from 'models/user';
import { getExternalTechnicians } from 'apollo/templates/user';
import ConnectedExternalTechnicians from './connected-external-technician.component';
import AddIcon from '@mui/icons-material/Add';
import { addLinkedExtTechTemplate, getOrderForLinkedExtTechTemplate } from 'apollo/templates/order';
import { useMutation } from '@apollo/client/react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { LinkedUser } from 'models/order';

type ManageExternalTechniciansProps = {
    orderId: string;
};
const ManageExternalTechnicians: FC<ManageExternalTechniciansProps> = ({ orderId }: ManageExternalTechniciansProps) => {
    const { t: translate } = useTranslation('orders');
    const [externalTechnicians, setExternalTechnicians] = useState<User[]>();
    const [notConnectedTechnicians, setNotConnectedTechnicians] = useState<User[]>();
    const [connectedTechnicians, setConnectedTechnicians] = useState<LinkedUser[]>();
    const [loading, setLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(getExternalTechnicians, {
        onCompleted: ({ technicians }) => {
            setExternalTechnicians(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(getOrderForLinkedExtTechTemplate, {
        variables: { orderId: orderId },
        onCompleted: ({ order }) => {
            setConnectedTechnicians(order.linkedExtTechs || []);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [addLinkedExtTech] = useMutation(addLinkedExtTechTemplate, {
        onCompleted: ({ addLinkedExtTech }) => {
            if (connectedTechnicians) {
                const newTech = addLinkedExtTech.linkedExtTechs.slice(-1)[0];
                setConnectedTechnicians([...connectedTechnicians, newTech]);
            }
            setLoading(false);
        },
        onError(error) {
            setLoading(false);
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (externalTechnicians && connectedTechnicians) {
            const notConnected = externalTechnicians.filter((tech) => {
                return !connectedTechnicians.find((connectedTech) => {
                    return connectedTech.user._id === tech._id;
                });
            });
            setNotConnectedTechnicians(notConnected);
            setLoading(false);
        }
    }, [connectedTechnicians, externalTechnicians]);

    const addExternalTechnician = (externalTech: User) => {
        setLoading(true);
        addLinkedExtTech({ variables: { orderId: orderId, userId: externalTech._id } });
    };

    const handleRemoveExternalTechnician = (technician: User) => {
        if (connectedTechnicians) {
            setConnectedTechnicians(connectedTechnicians.filter((connectedTech) => connectedTech.user !== technician));
        }
    };

    return (
        <Box>
            <Box>
                {loading ? <DelayedLinearProgress /> : undefined}
                <Autocomplete
                    fullWidth
                    disableClearable
                    options={notConnectedTechnicians || []}
                    getOptionLabel={(technician) => technician.fullName}
                    renderOption={(props, technician) => (
                        <MenuItem {...props} key={technician._id} value={technician._id}>
                            <ListItemIcon>
                                <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{technician.fullName}</ListItemText>
                        </MenuItem>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={translate('ADD_EXTERNAL_USER')}
                            data-test="add-external-user-input"
                        />
                    )}
                    inputValue={inputValue}
                    onChange={(_, technician) => {
                        addExternalTechnician(technician);
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'reset') {
                            setInputValue('');
                            return;
                        }
                        setInputValue(value);
                    }}
                />
            </Box>
            <Box mt={2}>
                {connectedTechnicians ? (
                    <ConnectedExternalTechnicians
                        orderId={orderId}
                        onRemove={handleRemoveExternalTechnician}
                        connectedExternalTechnicians={connectedTechnicians}
                    />
                ) : undefined}
            </Box>
        </Box>
    );
};

export default ManageExternalTechnicians;
